import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useListCoaches } from "@workspace/api-client-react";
import { useOrg } from "@/lib/orgContext";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Users, Download, Upload, Zap, FileSpreadsheet, FileText, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { useToast } from "@/hooks/use-toast";
import { ImportModal } from "@/components/ImportModal";
import { SampleBadge } from "@/components/SampleBadge";
import { SeatUsageBanner } from "@/components/SeatUsageBanner";
import { getEffectiveCoachLimit, nextPlan, PLAN_META, hasFeature, type Plan } from "@workspace/plans";

async function triggerDownload(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

function contractBadge(status: string | null | undefined) {
  if (!status) return null;
  const variants: Record<string, "green" | "gold" | "red" | "muted" | "secondary"> = {
    active: "green",
    pending: "gold",
    expired: "red",
    terminated: "muted",
  };
  return <Badge variant={variants[status] ?? "secondary"}>{status}</Badge>;
}

function insuranceBadge(status: string | null | undefined) {
  const s = status ?? "unknown";
  const variants: Record<string, "green" | "red" | "gold" | "muted"> = {
    compliant: "green",
    issues: "gold",
    missing: "red",
  };
  return <Badge variant={variants[s] ?? "muted"}>{s.replace("_", " ")}</Badge>;
}

type CfDef = { id: number; key: string; label: string; type: string };

export default function Coaches() {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { toast } = useToast();
  const { org } = useOrg();
  const { getToken } = useAuth();
  const isAdmin = org?.role === "admin";

  const { data: coaches, isLoading } = useListCoaches({ search: search || undefined });
  const { data: allCoaches } = useListCoaches({});

  // Custom field definitions and batch values for list columns
  const [cfDefs, setCfDefs] = useState<CfDef[]>([]);
  const [cfValues, setCfValues] = useState<Record<number, Record<string, unknown>>>({});

  useEffect(() => {
    let cancelled = false;
    async function fetchCfDefs() {
      const token = await getToken();
      const res = await fetch("/api/custom-fields/definitions?entityType=coach", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || cancelled) return;
      const defs = (await res.json()) as CfDef[];
      if (!cancelled) setCfDefs(defs.filter((d: CfDef & { enabled?: boolean }) => d.enabled !== false));
    }
    fetchCfDefs();
    return () => { cancelled = true; };
  }, [getToken]);

  useEffect(() => {
    if (!coaches || coaches.length === 0 || cfDefs.length === 0) return;
    let cancelled = false;
    async function fetchBatch() {
      const token = await getToken();
      const ids = (coaches ?? []).map((c) => c.id).join(",");
      const res = await fetch(`/api/custom-fields/values/batch?entityType=coach&entityIds=${ids}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok || cancelled) return;
      const data = (await res.json()) as { definitions: CfDef[]; values: Record<number, Record<string, unknown>> };
      if (!cancelled) setCfValues(data.values ?? {});
    }
    fetchBatch();
    return () => { cancelled = true; };
  }, [coaches, cfDefs, getToken]);
  const currentPlan: Plan = (org?.plan as Plan | undefined) ?? "free";
  // Demo orgs never see seat-limit upsell banners (pricing must not surface
  // during sales demos; billing is handled by the account team).
  const isDemo = Boolean(org?.isDemo);
  const planLimit = getEffectiveCoachLimit(currentPlan, org?.purchasedSeats ?? 1);
  // Only active and on_leave coaches count toward the seat cap.
  // Inactive coaches do not consume a seat.
  const totalCoaches = allCoaches?.filter((c) => c.status === "active" || c.status === "on_leave").length ?? 0;
  const atLimit = planLimit !== null && totalCoaches >= planLimit;
  const planName = PLAN_META[currentPlan as Plan]?.name ?? currentPlan;
  const upgradePlan = nextPlan(currentPlan);
  const nextPlanName = PLAN_META[upgradePlan]?.name ?? upgradePlan;
  const canImport = hasFeature(currentPlan, "coaches.bulk_import");

  const handleExport = async (format: "csv" | "xlsx") => {
    setIsExporting(true);
    const date = new Date().toISOString().split("T")[0];
    const url = format === "xlsx"
      ? "/api/export/coaches.csv?format=xlsx"
      : "/api/export/coaches.csv";
    try {
      await triggerDownload(url, `coaches-${date}.${format}`);
    } catch {
      toast({ title: "Export failed", description: `Unable to download coaches ${format.toUpperCase()}.`, variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <ImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Import Coaches"
        description="Upload a CSV file to bulk-import coach profiles. Download the template first to see the required format."
        templateEndpoint="/api/import/coaches/template"
        importEndpoint="/api/import/coaches"
        templateFilename="coaches-import-template.csv"
        invalidateKey={["listCoaches"]}
      />

      {/* Page header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Coaches</h1>
          <p>Manage personnel profiles and compliance statuses across your program.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!canImport) {
                  toast({ title: "Starter plan required", description: "CSV imports require the Starter plan or higher.", variant: "destructive" });
                  setLocation("/settings#plans");
                  return;
                }
                setImportOpen(true);
              }}
            >
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Import CSV
              {!canImport && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-gold font-bold">Starter</span>}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isExporting}>
                <Download className="h-3.5 w-3.5 mr-1.5" />
                {isExporting ? "Exporting…" : "Export"}
                <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport("csv")}>
                <FileText className="h-3.5 w-3.5 mr-2" />
                CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("xlsx")}>
                <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
                Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {isAdmin && (
            <Button
              onClick={() => {
                if (atLimit) {
                  toast({ title: "Seat limit reached", description: `Your ${planName} plan is limited to ${planLimit} coaches. Upgrade to ${nextPlanName} or add more seats.`, variant: "destructive" });
                  setLocation("/settings#plans");
                  return;
                }
                setLocation("/coaches/new");
              }}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add Coach
            </Button>
          )}
        </div>
      </div>

      {/* Seat usage warning banner — per-seat plans (track / draft) */}
      {org && !isDemo && (currentPlan === "track" || currentPlan === "draft") && (
        <SeatUsageBanner
          orgId={org.id}
          plan={currentPlan}
          purchasedSeats={org.purchasedSeats ?? 0}
          coachCount={totalCoaches}
        />
      )}

      {/* Generic seat usage banner — non per-seat plans */}
      {planLimit !== null && !isDemo && currentPlan !== "track" && currentPlan !== "draft" && (
        <div className={`rounded-xl border p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${atLimit ? "border-orange-400/50 bg-orange-500/8" : "border-border bg-card"}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${atLimit ? "bg-orange-500/15" : "bg-muted"}`}>
              <Zap className={`h-4 w-4 ${atLimit ? "text-orange-500" : "text-muted-foreground"}`} />
            </div>
            <div>
              <p className="text-sm font-semibold kpi-value">
                {totalCoaches} / {planLimit} <span className="font-normal text-muted-foreground">coach seats used</span>
              </p>
              <p className="text-xs text-muted-foreground">
                {atLimit
                  ? `You've reached your ${planName} plan limit. Upgrade to ${nextPlanName} or add more seats to keep adding coaches.`
                  : `${planName} plan includes up to ${planLimit} coach profiles.`}
              </p>
            </div>
          </div>
          {atLimit && isAdmin && (
            <Button size="sm" onClick={() => setLocation("/settings#plans")}>
              Upgrade to {nextPlanName}
            </Button>
          )}
        </div>
      )}

      {/* Coaches table */}
      <div className="rounded-xl border overflow-hidden bg-card">
        <div className="p-4 border-b">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, sport, or title…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
            </div>
          ) : coaches && coaches.length > 0 ? (
            coaches.map((coach) => (
              <button
                key={coach.id}
                type="button"
                onClick={() => setLocation(`/coaches/${coach.id}`)}
                className="w-full text-left p-4 space-y-2 hover:bg-muted/40 active:bg-muted transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5 flex-wrap">
                      <span>{coach.firstName} {coach.lastName}</span>
                      {coach.isSample && <SampleBadge />}
                    </div>
                    <div className="text-xs text-muted-foreground">{coach.sport} · {coach.title}</div>
                  </div>
                  <Badge variant={coach.status === "active" ? "green" : "muted"}>{coach.status.replace("_", " ")}</Badge>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {contractBadge(coach.contractStatus) ?? <span className="text-xs text-muted-foreground italic">No contract</span>}
                  {insuranceBadge(coach.insuranceStatus)}
                  {coach.programGender && (
                    <Badge variant="muted">
                      {coach.programGender === "mens" ? "Men's" : coach.programGender === "womens" ? "Women's" : "Co-ed"}
                    </Badge>
                  )}
                </div>
              </button>
            ))
          ) : (
            <div className="p-4">
              {search ? (
                <EmptyState icon={Users} title="No coaches match your search" description={`No results for "${search}". Try a different name, sport, or title.`} variant="compact" />
              ) : (
                <EmptyState icon={Users} title="No coaches on file yet" description="Add your first coach profile to start tracking contracts, insurance, and compliance." action={isAdmin ? { label: "Add your first coach", href: "/coaches/new" } : undefined} />
              )}
            </div>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block">
          <Table className="min-w-[760px] table-refined">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Sport / Title</TableHead>
                <TableHead>Program</TableHead>
                <TableHead>Contract</TableHead>
                <TableHead>Insurance</TableHead>
                <TableHead>Status</TableHead>
                {cfDefs.map((d) => (
                  <TableHead key={d.key} title={d.label} className="max-w-[120px] truncate">{d.label}</TableHead>
                ))}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[150px] skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px] skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-[80px] rounded-md skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-[80px] rounded-md skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-[80px] rounded-md skeleton-shimmer" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-[70px] rounded-md skeleton-shimmer" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-8 w-[60px] ml-auto skeleton-shimmer" /></TableCell>
                  </TableRow>
                ))
              ) : coaches && coaches.length > 0 ? (
                coaches.map((coach) => (
                  <TableRow key={coach.id} className="cursor-pointer" onClick={() => setLocation(`/coaches/${coach.id}`)}>
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span>{coach.firstName} {coach.lastName}</span>
                        {coach.isSample && <SampleBadge />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm">{coach.sport}</span>
                        <span className="text-xs text-muted-foreground">{coach.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {coach.programGender ? (
                        <Badge variant="muted">
                          {coach.programGender === "mens" ? "Men's" : coach.programGender === "womens" ? "Women's" : "Co-ed"}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">unassigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contractBadge(coach.contractStatus) ?? <span className="text-xs text-muted-foreground italic">none</span>}
                    </TableCell>
                    <TableCell>
                      {insuranceBadge(coach.insuranceStatus)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={coach.status === "active" ? "green" : "muted"}>{coach.status.replace("_", " ")}</Badge>
                    </TableCell>
                    {cfDefs.map((d) => {
                      const raw = cfValues[coach.id]?.[d.key];
                      const display = raw == null ? "" : d.type === "checkbox" ? (raw ? "Yes" : "No") : String(raw);
                      return (
                        <TableCell key={d.key} className="max-w-[120px] truncate text-xs text-muted-foreground" title={display}>
                          {display || <span className="italic">—</span>}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" asChild>
                        <Link href={`/coaches/${coach.id}`}>Profile</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7 + cfDefs.length} className="p-0">
                    {search ? (
                      <EmptyState icon={Users} title="No coaches match your search" description={`No results for "${search}". Try a different name, sport, or title.`} variant="compact" />
                    ) : (
                      <EmptyState icon={Users} title="No coaches on file yet" description="Add your first coach profile to start tracking contracts, insurance, and compliance." action={isAdmin ? { label: "Add your first coach", href: "/coaches/new" } : undefined} />
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
